import type { MockPersona } from '@eis/core';
import personaFixtures from '../fixtures/personas.json' with { type: 'json' };

/**
 * Test personas (Addendum A.3): the ONLY way stories and BDD scenarios reference permissions.
 * A scenario says `Given I am logged in as "support-read-only"`, never a permission string —
 * which keeps Gherkin business-readable and keeps permission changes in one place.
 *
 * Grants come straight from `fixtures/personas.json`; they are not restated here.
 */
export type PersonaId =
  | 'admin'
  | 'operations-manager'
  | 'product-configurator'
  | 'support-read-only'
  | 'finance-clerk'
  | 'claims-handler'
  /**
   * Grants in no domain any registered module declares.
   *
   * It exists so "a user with no products assigned" stays a testable state. Every other persona
   * holds something in `policy-management`, and prompt 11.3 gave the claims handler a Policen
   * entry it legitimately has — a claims handler holds `policies.view` and the nav is filtered by
   * exactly that declaration (Addendum A.2). Without this persona the shell's no-access page
   * would be unreachable, which is not the same as being wrong.
   */
  | 'translation-manager'
  /** The Data Suite's analyst (SDD-024 A-7, §10): every reporting action but the settings. */
  | 'analyst';

const raw = personaFixtures as Record<string, { displayName: string; grants: string[] }>;

const toPersona = (id: string): MockPersona => {
  const entry = raw[id];
  if (entry === undefined)
    throw new Error(`Persona "${id}" is missing from fixtures/personas.json.`);
  return { id, displayName: entry.displayName, grants: entry.grants };
};

export const personaIds: readonly PersonaId[] = [
  'admin',
  'operations-manager',
  'product-configurator',
  'support-read-only',
  'finance-clerk',
  'claims-handler',
  'translation-manager',
  'analyst',
];

export const personas: Readonly<Record<PersonaId, MockPersona>> = Object.freeze(
  Object.fromEntries(personaIds.map((id) => [id, toPersona(id)])) as Record<PersonaId, MockPersona>,
);

export const DEFAULT_PERSONA: PersonaId = 'operations-manager';

/** Maps the human label used in Gherkin (`"operations manager"`) to a persona id. */
export function personaIdFromLabel(label: string): PersonaId {
  const normalized = label.trim().toLowerCase().replaceAll(' ', '-');
  if (personaIds.includes(normalized as PersonaId)) return normalized as PersonaId;
  const byDisplayName = personaIds.find(
    (id) => personas[id].displayName.toLowerCase() === label.trim().toLowerCase(),
  );
  if (byDisplayName !== undefined) return byDisplayName;
  throw new Error(
    `Unknown persona "${label}". Known personas: ${personaIds.join(', ')} (fixtures/personas.json).`,
  );
}
